import { DisaggregatedMetric, GeoDetails, GeoTag, MetricMetadata } from "../services/types";

import printWhiteSvg from '../assets/print-white.svg';
import portImg from '../assets/Port of Seattle Logo.png';
import equitySvg from '../assets/Equity.svg';
import accessibilitySvg from '../assets/Accessibility.svg';
import livabilitySvg from '../assets/Livability.svg';
import economySvg from '../assets/economy.svg';
import environmentSvg from '../assets/Environment.svg';
import treeSvg from '../assets/tree.svg';

import PDFMetricCard from "../components/pdf/PDFMetricCard";
import MetricCategoriesContext from "../context/MetricCategoriesContext";
import { useContext, useEffect, useState } from "react";
import { categorizeData, getIndexMetricDataPDF, getMetricGroupChartId, getSortedMetrics, getSvgMap, getSvgrMap, orgId } from "../utils/dashboardUtils";
import LoadingIndicator from "../components/LoadingIndicator";
import TitleBanner from "../components/pdf/TitleBanner";
import DisaggregateMetricCard from "../components/DisaggregateMetricCard";
import SingleBar from "../components/charts/SingleBar";
import SimpleDonutChart from "../components/charts/SimpleDonutChart";
import { indicatorsEnv } from "../utils/environment";
import RaceEthnicityBarChartCard from "../components/charts/RaceEthnicityBarChartCard";
import MetricBarChart from "../components/charts/MetricBarChart";
import DoubleMetricCard from "../components/metrics/DoubleMetricCard";
import { useDashboardColor } from "../context/DashboardColorContext";


interface MetricsModalProps {
    isOpen: boolean;
    onClose: (e: React.MouseEvent<HTMLButtonElement>) => void;
    geoDetails: GeoDetails[];
    metadata: MetricMetadata[] | null;
    geoTag: GeoTag | null;
    pdfUrl: string;
    logoUrl: string;
    legendUrl: string;
    selectedArea: string;
    siteUrl: string;
    selectionActive: boolean;
}

const MetricsModal: React.FC<MetricsModalProps> = ({ isOpen, onClose, metadata, geoDetails, geoTag, pdfUrl, logoUrl, legendUrl, siteUrl, selectedArea, selectionActive }) => {
    const { metricCategories } = useContext(MetricCategoriesContext);
    const [loading, setLoading] = useState(true);
    const [disaggregatedData, setDisaggregatedData] = useState<DisaggregatedMetric[]>([]);
    const color = useDashboardColor(); 
    const geoEncoding = 'delta';
    const indicatorsUrl = `${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getallindicatorsv2`;

    const fetchData = async () => {
        if (!isOpen || !orgId || metricCategories.length === 0) return;
    
        try {
            setLoading(true);
    
            if (orgId !== null) {
                // Generate an array of fetch promises, one for each category
                const fetchPromises = metricCategories.map(category => 
                    fetch(indicatorsUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            geoDetails,
                            orgId,
                            dashboardView: category,
                            geoEncoding,
                            geoTag,
                            allowDefault: true,
                            environment: indicatorsEnv
                        }),
                    }).then(response => {
                        if (!response.ok) {
                            throw new Error(`Failed to fetch data for ${category}. Response: ${response}`);
                        }
                        return response.json();
                    })
                );
    
                // Resolve all promises in parallel
                const results = await Promise.all(fetchPromises);
                console.log('results from category calls:', results);
    
                // Combine and categorize the results
                const combinedData = results.flat();
                setDisaggregatedData(combinedData);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            // setError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen && metricCategories.length > 0) {
            console.log('Effect triggered:', { isOpen, geoDetails, orgId, metricCategories });
            fetchData();
        }
    }, [isOpen, geoDetails, orgId, metricCategories]);

    console.log('disaggreagted data:', disaggregatedData);
    
    // Function to categorize the data based on category
    // const categorizeData = (data: DisaggregatedMetric[]) => {
    //     return metricCategories.map(category => {
    //         // Filter metrics by category
    //         const filteredMetrics = data.filter(item => item.category === category);
    //         return {
    //             category,
    //             metrics: filteredMetrics
    //         };
    //     });
    // };

    // Categorize the disaggregated data
    const categorizedData = categorizeData(disaggregatedData);

    // Get sorted metrics for each dashboard panel
    const sortedDemographicMetrics = getSortedMetrics('Demographics', categorizedData);
    const sortedAccessibilityMetrics = getSortedMetrics('Accessibility', categorizedData);
    const sortedLivabilityMetrics = getSortedMetrics('Livability', categorizedData);
    const sortedEconomyMetrics = getSortedMetrics('Economy', categorizedData);
    const sortedEnvironmentMetrics = getSortedMetrics('Environment', categorizedData);

    // Get index metric data for each dashboard panel
    const indexMetricDataDemographic = getIndexMetricDataPDF('Demographics', metadata, disaggregatedData);
    const indexMetricDataAccessibility = getIndexMetricDataPDF('Accessibility', metadata, disaggregatedData);
    const indexMetricDataLivability = getIndexMetricDataPDF('Livability', metadata, disaggregatedData);
    const indexMetricDataEconomy = getIndexMetricDataPDF('Economy', metadata, disaggregatedData);
    const indexMetricDataEnvironment = getIndexMetricDataPDF('Environment', metadata, disaggregatedData);

    // Define metrics for order
    // Demographic
    const demoOne: DisaggregatedMetric[] = sortedDemographicMetrics[0];
    const demoTwo: DisaggregatedMetric[] = sortedDemographicMetrics[1];
    const race = disaggregatedData.find(i => i.id === 354);
    const ethnicity = disaggregatedData.find(i => i.id === 1177);
    const immigration = disaggregatedData.find(i => i.id === 1180);
    const atHomeLanguage = disaggregatedData.find(i => i.id === 355);

    // Accessibility
    const a: DisaggregatedMetric[] = sortedAccessibilityMetrics[0];
    const a2: DisaggregatedMetric[] = sortedAccessibilityMetrics[1];

    // Livability
    const l: DisaggregatedMetric[] = sortedLivabilityMetrics[0];

    const homeOwnership = l.find(metric => metric.id === 336) || null;
    const ownerCostBurden = l.find(metric => metric.id === 334) || null;
    const renterCostBurden = l.find(metric => metric.id === 341) || null;

    const l2: DisaggregatedMetric[] = sortedLivabilityMetrics[1];
    const l3: DisaggregatedMetric[] = sortedLivabilityMetrics[2];
    const l4: DisaggregatedMetric[] = sortedLivabilityMetrics[3];

    const personalCrime = l4.find(metric => metric.id === 338) || null;
    const propertyCrime = l4.find(metric => metric.id === 342) || null;

    // Economy
    const e: DisaggregatedMetric[] = sortedEconomyMetrics[0];

    const bachelors = disaggregatedData.find(metric => metric.id === 323);
    const ged = disaggregatedData.find(metric => metric.id === 325);
    const masters = disaggregatedData.find(metric => metric.id === 326);
    const estimatedJobs = disaggregatedData.find(metric => metric.id === 328);

    // Environment
    const env: DisaggregatedMetric[] = sortedEnvironmentMetrics[0];

    const handlePrint = () => {
        const originalTitle = document.title;
        document.title = "DashboardMetrics";  // Set your desired title here
        window.print();
        document.title = originalTitle;  // Restore the original title
    };

    if (loading) {
        return <LoadingIndicator/>
    }

    return (
        <div id="print-container" className="fixed inset-0 bg-white p-4 overflow-y-auto z-50">
            {/* Wrapper to limit the modal's width */}
            <div className="max-w-[800px] mx-auto w-full mb-8">
                <div className="relative h-full w-full">
                    {/* Print and Close buttons */}
                    <div className="flex justify-center items-center no-print mb-[1rem]">
                        <button 
                            onClick={handlePrint}
                            className="flex mx-2 bg-[#0A5F75] border border-[#0A5F75] text-white py-1 pl-7 pr-8 rounded-md"
                        >
                            <img src={printWhiteSvg} alt="Print" className="w-3 h-3 mr-2 mt-[0.5px]" />
                            <p className="text-xs font-bold">Print</p>
                        </button>
                        <button 
                            onClick={onClose}
                            className="bg-transparent border border-[#0A5F75] py-1 px-8 rounded-md hover:bg-color-cai-lightGray"
                        >
                            <p className="text-xs text-[#0A5F75] font-bold">Close</p>
                        </button>
                    </div>

                    {/* Page 1 */}
                    <div className="flex flex-col page">
                        <div className="flex flex-col items-center justify-center relative mb-4">
                            <div className="w-full h-auto p-2 flex justify-between items-center bg-[#4D4D4D] rounded-md">
                                <img src={portImg} alt='City of Lacey Logo' className="w-20 h-20"/>
                                <div className="flex flex-col items-start justify-start mx-4 w-full">
                                    <p className="text-white font-bold text-[18px]">Port of Seattle</p>
                                    <p className="text-xs text-white">{selectedArea || 'King County, WA'}</p>
                                    <p className="text-xs text-white font-bold">{siteUrl || ''}</p>
                                </div>
                            </div>
                        </div>
                        <div className="flex-grow">
                            <div className="relative inline-block">
                                {pdfUrl && <img src={pdfUrl} className="h-auto w-auto" alt="Esri Map Image" /> }
                                {/* Removing legend for now due to performance concerns */}
                                { <div className="flex justify-between">
                                    { legendUrl && <img src={legendUrl} className="h-[188px] w-[220px] ml-6 mt-[-5rem]" alt="Esri Legend Image" /> }
                                    { logoUrl && <img src={logoUrl} className="absolute bottom-4 right-2 h-10 w-30" alt="Esri Logo Image" /> }
                                </div> }
    
                            </div>
                        </div>
                        {/* Demographic Metrics */}
                        <div className="content">
                            <div className="bg-[#f8f8fa] shadow-md rounded-md mb-4">
                                <TitleBanner metric={indexMetricDataDemographic} geoDetails={geoDetails} geoTag={geoTag} svg={equitySvg} text="Demographics"/>
                                <p className="text-port-text mx-6 text-xs mt-2">Indicators shown below depict demographic details for the population within the selected area.</p>
                                <div className="grid grid-cols-4 gap-4 my-2 mx-4 pb-2">
                                    {demoOne.map(metric => (
                                        <PDFMetricCard
                                            key={metric.id}
                                            metric={metric}
                                            width="w-full"
                                            svg={getSvgrMap(metric.id)}
                                        />
                                    ))}
                                    {demoTwo.map(metric => (
                                        <PDFMetricCard
                                            key={metric.id}
                                            metric={metric}
                                            width="w-full"
                                            svg={getSvgrMap(metric.id)}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                        {/* <Footer/> */}
                    </div>
                    <div className="page">
                        <div className="content">
                            <div className="bg-[#f8f8fa] shadow-md rounded-md my-4 pb-4">
                                <TitleBanner metric={indexMetricDataDemographic} geoDetails={geoDetails} geoTag={geoTag} svg={equitySvg} text="Demographics"/>
                                <p className="text-port-text mx-6 text-xs mt-4">Indicators shown below depict demographic details for the population within the selected area.</p>
                                <div className="grid grid-cols-2 gap-4 m-4">
                                    <div className="col-span-2 w-full">
                                        <RaceEthnicityBarChartCard
                                            raceMetric={race}
                                            ethnicityMetric={ethnicity}
                                            disaggData={disaggregatedData}
                                            geoDetails={geoDetails}
                                            geoTag={geoTag}
                                            selectionActive={selectionActive}
                                            height="h-60"
                                            background="bg-white"
                                            barColor={"#4F92B1"}
                                            showMetadataIcon={false}
                                        />
                                    </div>
                                    <div className="col-span-1 w-full">
                                        <MetricBarChart 
                                            metric={immigration} 
                                            disaggData={disaggregatedData}
                                            geoDetails={geoDetails}
                                            geoTag={geoTag}
                                            selectionActive={selectionActive}
                                            height="h-40" 
                                            background="bg-white"
                                            barColor={"#4F92B1"}
                                            topFive={true}
                                            showMetadataIcon={false}
                                        />
                                    </div>
                                    <div className="col-span-1 w-full">
                                        <MetricBarChart 
                                            metric={atHomeLanguage} 
                                            disaggData={disaggregatedData}
                                            geoDetails={geoDetails}
                                            geoTag={geoTag}
                                            selectionActive={selectionActive}
                                            height="h-40" 
                                            background="bg-white"
                                            barColor={"#4F92B1"}
                                            topFive={true}
                                            showMetadataIcon={false}
                                        />
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Page 2: Accessibility*/}
                    <div className="page">
                        <div className="content">
                            <div className="bg-[#f8f8fa] shadow-md rounded-md my-4 pb-4">
                                <TitleBanner metric={indexMetricDataAccessibility} geoDetails={geoDetails} geoTag={geoTag} svg={accessibilitySvg} text="Accessiblity"/>
                                <div className="grid grid-cols-4 gap-4 m-4">
                                    {a.map(metric => (
                                        <PDFMetricCard
                                            key={metric.id}
                                            metric={metric}
                                            width="w-full"
                                            svg={getSvgrMap(metric.id)}
                                        />
                                    ))}
                                </div>
                                <div className="grid grid-cols-3 gap-4 mx-4">
                                    <div className="flex flex-col justify-start col-span-3 pl-2 pr-6 pt-4 border-t w-full">
                                        <img src={treeSvg} alt={"Tree icon"} className="mb-2 w-[50px] h-[50px]" />
                                        <p className="text-port-text text-xs font-bold">Proximity to Parks and Open Space</p>
                                    </div>

                                    {a2.map(metric => (
                                        <PDFMetricCard
                                            key={metric.id}
                                            metric={metric}
                                            width="w-full"
                                            svg={getSvgrMap(metric.id)}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Page 2: Livability*/}
                    <div className="page">
                        <div className="content bg-[#f8f8fa]">
                            <div className="shadow-md rounded-md my-8 pb-4">
                                <TitleBanner metric={indexMetricDataLivability} geoDetails={geoDetails} geoTag={geoTag} svg={livabilitySvg} text="Livability"/>
                                <div className="grid grid-cols-3 gap-4 m-4">
                                    {homeOwnership && (
                                        <PDFMetricCard
                                            key={homeOwnership.id}
                                            metric={homeOwnership}
                                            width="w-full"
                                            svg={getSvgrMap(homeOwnership.id)}
                                        />
                                    )}
                                    {l2.map(metric => (
                                        <PDFMetricCard
                                            key={metric.id}
                                            metric={metric}
                                            width="w-full"
                                            svg={getSvgrMap(metric.id)}
                                        />
                                    ))}
                                    {l3.map(metric => (
                                        <PDFMetricCard
                                            key={metric.id}
                                            metric={metric}
                                            width="w-full"
                                            svg={getSvgrMap(metric.id)}
                                        />
                                    ))}
                                    {renterCostBurden && ownerCostBurden && (
                                        <DoubleMetricCard 
                                            key={renterCostBurden.id}
                                            metricOne={renterCostBurden}
                                            metricTwo={ownerCostBurden}
                                            subtextOne="Renters"
                                            subtextTwo="Owners"
                                            title="Housing Cost Burden"
                                            metadataId={339}
                                            width="w-full"
                                            svg={getSvgrMap(339)}
                                            disaggData={disaggregatedData}
                                            geoDetails={geoDetails}
                                            geoTag={geoTag}
                                            selectionActive={selectionActive}
                                            showMetadataIcon={false}
                                            showBothMetadataIcons={false}
                                        />
                                    )}
                                    <div className="col-span-2">
                                        <div 
                                            className="relative overflow-hidden group hover:shadow-md transition-all border rounded-xl bg-white"
                                            style={{ 
                                                borderLeftWidth: "4px", borderLeftColor: color,
                                            }}
                                        >
                                            <div className="bg-white p-4">
                                                {categorizedData.Livability.filter(metric => 
                                                    [getMetricGroupChartId("Livability", "crimerisk")].includes(metric.id) // Crime Risk
                                                ).map(metric => (
                                                    <DisaggregateMetricCard
                                                        key={metric.id}
                                                        id={metric.id}
                                                        title={metric.name}
                                                        svg={getSvgMap(metric.id)}
                                                        disaggData={disaggregatedData}
                                                        geoDetails={geoDetails}
                                                        geoTag={geoTag}
                                                        selectionActive={selectionActive}
                                                        showMetadataIcon={false}
                                                    />
                                                ))}
                                                <div className="relative w-full">
                                                    {/* Vertical red line at 200px, covering both bars */}
                                                    <div className="absolute top-0 bottom-0 w-[2px]" style={{ left: '280px', backgroundColor: 'rgba(239, 68, 68, 0.4)' }}></div>

                                                {personalCrime && 
                                                <SingleBar
                                                    key={personalCrime.id}
                                                    metric={personalCrime}
                                                    name='Personal'
                                                    width="w-full"
                                                    svg={getSvgMap(personalCrime.id)}
                                                    disaggData={disaggregatedData}
                                                    geoDetails={geoDetails}
                                                    geoTag={geoTag}
                                                    selectionActive={selectionActive}
                                                    showMetadataIcon={false}
                                                />
                                                }

                                                {propertyCrime && 
                                                <>
                                                <SingleBar
                                                    key={propertyCrime.id}
                                                    metric={propertyCrime}
                                                    name="Property"
                                                    width="w-full"
                                                    svg={getSvgMap(propertyCrime.id)}
                                                    disaggData={disaggregatedData}
                                                    geoDetails={geoDetails}
                                                    geoTag={geoTag}
                                                    selectionActive={selectionActive}
                                                    showMetadataIcon={false}
                                                />
                                                
                                                </>
                                                }
                                                <p className="text-color-cai-gray text-[9px] absolute -bottom-4" style={{ left: '245px' }}>National Average</p>
                                                </div>
                                                
                                                <p className="text-[#4D4D4D] text-[11px] ml-2 my-4">{categorizedData.Livability.filter(metric => 
                                                    [getMetricGroupChartId("Livability", "crimerisk")].includes(metric.id) // Crime Risk
                                                ).map(metric => metric.versions[0].unit_descriptor)}</p>
                                            </div>
                                            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#2258e5]/50 to-[#00ac4e]/50 opacity-0 group-hover:opacity-100 transition-opacity" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* <Footer /> */}
                    </div>

                    {/* Page 3: Economy */}
                    <div className="page">
                        <div className="content">
                            <div className="bg-[#f8f8fa] shadow-md rounded-md my-8">
                                <TitleBanner metric={indexMetricDataEconomy} geoDetails={geoDetails} geoTag={geoTag} svg={economySvg} text="Economy"/>
                                <div className="grid grid-cols-4 gap-4 m-4">
                                    {e.map(metric => (
                                        <PDFMetricCard
                                            key={metric.id}
                                            metric={metric}
                                            width="w-full"
                                            svg={getSvgrMap(metric.id)}
                                        />
                                    ))}

                                    <div className="col-span-2 w-full pr-4">
                                        <div 
                                            className="relative overflow-hidden group hover:shadow-md transition-all border rounded-xl bg-white" 
                                            style={{ 
                                                borderLeftWidth: "4px", borderLeftColor: color,
                                            }}
                                        >
                                            <div className="bg-white p-4">
                                                <h3 className="text-sm font-semibold text-port-text mb-2">
                                                    Educational Attainment
                                                </h3>
                                                <div className="flex items-start justify-center gap-4">
                                                    {masters && 
                                                    <div className="flex flex-col items-start">

                                                        <SimpleDonutChart indicators={disaggregatedData} currentTab={"Economy"} chartName="masters" fillColor="#4F92B1" remainColor="#D5D5D5" valueColor="#4D4D4D"/>
                                                        <p className="text-port-text text-xs font-semibold mt-2">{masters.name}</p>
                                                    </div>
                                                    }
                                                    {bachelors && 
                                                    <div className="flex flex-col items-start">
                                                        <SimpleDonutChart indicators={disaggregatedData} currentTab={"Economy"} chartName="bachelors" fillColor="#4F92B1" remainColor="#D5D5D5" valueColor="#4D4D4D"/>
                                                        <p className="text-port-text text-xs font-semibold mt-2">{bachelors.name}</p>
                                                        <div className="mt-0.5 rounded-lg h-4 w-10 px-1.5 text-[10px] text-white text-center" style={{ backgroundColor: color }}>Index</div>
                                                    </div>
                                                    }
                                                    {ged && 
                                                    <div className="flex flex-col items-start">
                                                        <SimpleDonutChart indicators={disaggregatedData} currentTab={"Economy"} chartName="ged" fillColor="#4F92B1" remainColor="#D5D5D5" valueColor="#4D4D4D"/>
                                                        <p className="text-port-text text-xs font-semibold mt-2">{ged.name}</p>
                                                    </div>
                                                    }
                                                </div>
                                            </div>
                                            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#2258e5]/50 to-[#00ac4e]/50 opacity-0 group-hover:opacity-100 transition-opacity" />
                                        </div>
                                    </div>

                                    <div className="col-span-2 w-full pr-4 mb-4">
                                        <MetricBarChart 
                                            metric={estimatedJobs} 
                                            disaggData={disaggregatedData}
                                            geoDetails={geoDetails}
                                            geoTag={geoTag}
                                            selectionActive={selectionActive}
                                            height="h-48" 
                                            background="bg-white"
                                            barColor={"#4F92B1"}
                                            subText="Total Jobs:"
                                            showValue={true}
                                            showMetadataIcon={false}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Page 4: Environment */}
                    <div className="page">
                        <div className="bg-[#f8f8fa] shadow-md rounded-md my-8 pb-4">
                            <TitleBanner metric={indexMetricDataEnvironment} geoDetails={geoDetails} geoTag={geoTag} svg={environmentSvg} text="Environment"/>
                            <div className="grid grid-cols-3 gap-4 m-4">
                                {env.map(metric => (
                                    <PDFMetricCard
                                        key={metric.id}
                                        metric={metric}
                                        width="w-full"
                                        svg={getSvgrMap(metric.id)}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MetricsModal;